import { marketingBackendApiUrl } from '../config';

export type VenueCategory = {
  slug: string;
  label: string;
  group: string;
  requiresAge21?: boolean;
};

/**
 * How many kinds one venue may claim. The server enforces its own cap regardless; this is only what
 * the form tells a person before they hit it.
 */
export const DEFAULT_MAX_VENUE_CATEGORIES = 5;

/**
 * A bundled copy of the backend catalog (models.VenueCategories).
 *
 * The form fetches the live list on mount and uses this only if that fails, so the page still works
 * offline or against an old backend. Drift is harmless in both directions: the server drops slugs it
 * does not recognise, and a slug added upstream shows up as soon as the fetch succeeds — the bundle
 * never has to be right, only present.
 */
export const FALLBACK_VENUE_CATEGORIES: VenueCategory[] = [
  { slug: 'coffee_shop', label: 'Coffee shop', group: 'Food & drink' },
  { slug: 'cafe_tea', label: 'Tea house / matcha bar', group: 'Food & drink' },
  { slug: 'restaurant', label: 'Restaurant', group: 'Food & drink' },
  { slug: 'brunch_spot', label: 'Brunch spot', group: 'Food & drink' },
  { slug: 'bakery_dessert', label: 'Bakery / dessert shop', group: 'Food & drink' },
  { slug: 'food_hall_market', label: 'Food hall / market', group: 'Food & drink' },
  { slug: 'bar_lounge', label: 'Bar / cocktail lounge', group: 'Nightlife', requiresAge21: true },
  { slug: 'brewery_taproom', label: 'Brewery / taproom', group: 'Nightlife', requiresAge21: true },
  { slug: 'winery_wine_bar', label: 'Winery / wine bar', group: 'Nightlife', requiresAge21: true },
  { slug: 'dance_club', label: 'Club / dance night', group: 'Nightlife', requiresAge21: true },
  { slug: 'live_music_venue', label: 'Live music venue', group: 'Live & performance' },
  { slug: 'comedy_club', label: 'Comedy / improv club', group: 'Live & performance' },
  { slug: 'karaoke', label: 'Karaoke', group: 'Live & performance' },
  { slug: 'theater_cinema', label: 'Theatre / cinema', group: 'Live & performance' },
  { slug: 'bookstore', label: 'Bookstore', group: 'Arts & making' },
  { slug: 'museum_gallery', label: 'Museum / gallery', group: 'Arts & making' },
  { slug: 'art_craft_studio', label: 'Art / craft studio', group: 'Arts & making' },
  { slug: 'makerspace', label: 'Makerspace / workshop', group: 'Arts & making' },
  { slug: 'board_game_cafe', label: 'Board game cafe', group: 'Games & play' },
  { slug: 'arcade', label: 'Arcade / gaming lounge', group: 'Games & play' },
  { slug: 'escape_room', label: 'Escape room', group: 'Games & play' },
  { slug: 'bowling_mini_golf', label: 'Bowling / mini golf', group: 'Games & play' },
  { slug: 'trivia_venue', label: 'Trivia / quiz night', group: 'Games & play' },
  { slug: 'climbing_gym', label: 'Climbing gym', group: 'Active' },
  { slug: 'fitness_studio', label: 'Gym / fitness studio', group: 'Active' },
  { slug: 'yoga_wellness', label: 'Yoga / wellness studio', group: 'Active' },
  { slug: 'sports_facility', label: 'Sports facility / league', group: 'Active' },
  { slug: 'outdoor_rental', label: 'Outdoor outfitter / rentals', group: 'Active' },
  { slug: 'park_outdoor_space', label: 'Park / outdoor space', group: 'Active' },
  { slug: 'community_space', label: 'Community space', group: 'Community' },
  { slug: 'lgbtq_space', label: 'LGBTQ+ space', group: 'Community' },
  { slug: 'faith_community', label: 'Faith community', group: 'Community' },
  { slug: 'pet_friendly', label: 'Pet-friendly spot', group: 'Community' },
];

/** groupVenueCategories preserves catalog order for both the groups and the chips inside them. */
export function groupVenueCategories(
  categories: VenueCategory[]
): { group: string; categories: VenueCategory[] }[] {
  const order: string[] = [];
  const byGroup = new Map<string, VenueCategory[]>();
  for (const c of categories) {
    const group = c.group || 'Other';
    if (!byGroup.has(group)) {
      byGroup.set(group, []);
      order.push(group);
    }
    byGroup.get(group)!.push(c);
  }
  return order.map((group) => ({ group, categories: byGroup.get(group)! }));
}

function isVenueCategory(value: unknown): value is VenueCategory {
  if (typeof value !== 'object' || value === null) return false;
  const c = value as Record<string, unknown>;
  return typeof c.slug === 'string' && !!c.slug && typeof c.label === 'string' && !!c.label;
}

/**
 * Fetch the live category catalog. Never throws and never returns an empty list — the caller always
 * has something to render, because a category picker with nothing in it is worse than a stale one.
 */
export async function fetchVenueCategories(
  signal?: AbortSignal
): Promise<{ categories: VenueCategory[]; maxSelectable: number }> {
  const fallback = {
    categories: FALLBACK_VENUE_CATEGORIES,
    maxSelectable: DEFAULT_MAX_VENUE_CATEGORIES,
  };
  const endpoint = marketingBackendApiUrl('/api/partner-venue-categories');
  if (!endpoint) return fallback;
  try {
    const res = await fetch(endpoint, { headers: { Accept: 'application/json' }, signal });
    if (!res.ok) return fallback;
    const body: unknown = await res.json();
    const parsed = body as { categories?: unknown; maxSelectable?: unknown } | null;
    const categories = Array.isArray(parsed?.categories)
      ? parsed.categories.filter(isVenueCategory).map((c) => ({ ...c, group: c.group || 'Other' }))
      : [];
    if (!categories.length) return fallback;
    const max =
      typeof parsed?.maxSelectable === 'number' && parsed.maxSelectable > 0
        ? parsed.maxSelectable
        : DEFAULT_MAX_VENUE_CATEGORIES;
    return { categories, maxSelectable: max };
  } catch {
    return fallback;
  }
}
